import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Banner from "../../Components/Banner";
import useSWR from "swr";
import { URLS } from "../../Services/urls";
import { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import Section from "../../Components/Section";
import ProductWrapper from "../../Components/Product/ProductWrapper";
import Nested from "../../Components/Nested";
import Input from "../../Components/Input";
import StepTitle from "../../Components/Title";
import ButtonNav from "../../Components/ButtonNav";
import { FaLongArrowAltRight } from "react-icons/fa";
import { FaLongArrowAltLeft } from "react-icons/fa"; 
import SectionImage from "../../Components/SectionImage";
import ChoiceItem from "../../Components/ChoiceItem";
import { setProjectType, setSectionNames } from "../../Redux/Slice/selectedProductsSlice";
import { useSelector } from "react-redux";
import NestedProductWrapper from "../../Components/Product/NestedProductWrapper";

const Project = () => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: project, isLoading } = useSWR(URLS.PROJECT.details(projectId))
  
  // Récupérer les données Redux pour debug
  const selectedProducts = useSelector(state => state.selectedProducts.selectedProducts);
  const userInputs = useSelector(state => state.selectedProducts.userInputs);
  const sectionNames = useSelector(state => state.selectedProducts.sectionNames);
  const selectedChoices = useSelector(state => state.selectedProducts.selectedChoices);
  
  // Debug: Afficher l'état Redux
  useEffect(() => {
    console.log('🔍 Project - selectedProducts:', selectedProducts);
    console.log('🔍 Project - userInputs:', userInputs);
    console.log('🔍 Project - selectedChoices:', selectedChoices);
  }, [selectedProducts, userInputs, selectedChoices]);

  const [activeStep, setActiveStep] = useState(0);
  const [selectedNestedSection, setSelectedNestedSection] = useState(null);
  const [selectedChoiceBySection, setSelectedChoiceBySection] = useState({});
  const sectionsMap = useMemo(() => {
    const map = {};

    const addSectionToMap = (section) => {
      if (!section || !section.id) return;
      map[section.id] = section;
      
      // Ajouter les nested_sections récursivement
      if (section.nested_sections && section.nested_sections.length > 0) {
        section.nested_sections.forEach(nestedSection => {
          addSectionToMap(nestedSection);
          // Ajouter aussi les sections à l'intérieur des nested_sections
          if (nestedSection.sections && Array.isArray(nestedSection.sections)) {
            nestedSection.sections.forEach(innerSection => {
              addSectionToMap(innerSection);
            });
          }
        });
      }
      
      // Ajouter les sections enfants si elles existent
      if (section.sections && Array.isArray(section.sections)) {
        section.sections.forEach(childSection => {
          addSectionToMap(childSection);
        });
      }
    };

    if (project?.steps) {
      project.steps.forEach(step => {
        step.sections?.forEach(section => {
          addSectionToMap(section);
          
          // IMPORTANT: Si la section a des choice_items (dans choices), s'assurer qu'ils sont bien dans choice_items
          // Certaines sections ont choice_items dans le champ "choices" au lieu de "choice_items"
          if (section.choices && Array.isArray(section.choices) && section.choices.length > 0) {
            if (!section.choice_items || section.choice_items.length === 0) {
              // Convertir choices en choice_items si nécessaire
              section.choice_items = section.choices;
              console.log(`✅ choice_items ajoutés depuis choices pour section ${section.id} (${section.name}): ${section.choice_items.length} choix`);
            }
          }
          
          // IMPORTANT: Ajouter aussi les sections qui sont référencées dans les parent_section
          // même si elles ne sont pas directement dans step.sections
          // Cela permet de découvrir les sections comme "PROFIL_BARDAGE_LAMES_FIBRES_CIMENT"
          // qui sont dans les parent_section des sections enfants
          if (section.parent_section && section.parent_section.id) {
            if (!map[section.parent_section.id]) {
              const parentSectionToAdd = {
                ...section.parent_section,
                choice_items: section.parent_section.choice_items || section.parent_section.choiceItems || section.parent_section.choices || []
              };
              map[section.parent_section.id] = parentSectionToAdd;
              console.log(`✅ parent_section découvert et ajouté à sectionsMap: ${section.parent_section.id} (${section.parent_section.name})`);
              console.log(`🔍 parent_section condition: ${section.parent_section.condition_from_prev_step || 'none'}`);
              console.log(`🔍 parent_section choice_items count:`, parentSectionToAdd.choice_items?.length || 0);
            }
          }
        });
      });
    }

    // Deuxième passe : ajouter les sections qui ont un parent_section et les parent_section eux-mêmes
    // Ces sections peuvent être référencées dans les children des choix mais ne pas être dans les steps directement
    if (project?.steps) {
      project.steps.forEach(step => {
        step.sections?.forEach(section => {
          // Si cette section a un parent_section, ajouter le parent_section à la map
          if (section.parent_section && section.parent_section.id) {
            // Ajouter le parent_section s'il n'est pas déjà dans la map
            if (!map[section.parent_section.id]) {
              // Le parent_section peut être un objet complet, l'ajouter directement
              if (section.parent_section.id && section.parent_section.name) {
                // S'assurer que le parent_section a bien ses choice_items
                const parentSectionToAdd = {
                  ...section.parent_section,
                  // S'assurer que choice_items est présent (peut être choice_items ou choiceItems selon le format)
                  choice_items: section.parent_section.choice_items || section.parent_section.choiceItems || []
                };
                map[section.parent_section.id] = parentSectionToAdd;
                console.log(`✅ parent_section ajouté à sectionsMap: ${section.parent_section.id} (${section.parent_section.name})`);
                console.log(`🔍 parent_section choice_items count:`, parentSectionToAdd.choice_items?.length || 0);
                if (parentSectionToAdd.choice_items && parentSectionToAdd.choice_items.length > 0) {
                  console.log(`🔍 parent_section choice_items:`, parentSectionToAdd.choice_items.map(c => ({ id: c.id, label: c.label, code: c.code })));
                }
              }
            } else {
              // Si le parent_section est déjà dans la map, s'assurer qu'il a bien ses choice_items
              const existingParent = map[section.parent_section.id];
              if (!existingParent.choice_items || existingParent.choice_items.length === 0) {
                if (section.parent_section.choice_items && section.parent_section.choice_items.length > 0) {
                  existingParent.choice_items = section.parent_section.choice_items;
                  console.log(`✅ choice_items ajoutés au parent_section existant: ${section.parent_section.id}`);
                }
              }
            }
            // Debug: Afficher les sections avec parent_section
            console.log(`🔍 Section avec parent_section trouvée: ${section.id} (${section.name}), parent: ${section.parent_section.id}`);
          }
          
          // Parcourir les choice_items pour trouver les sections enfants référencées
          section?.choice_items?.forEach(choice => {
            (choice.children || []).forEach(child => {
              // child peut être soit un ID (string/number), soit un objet complet
              const childId = typeof child === 'string' || typeof child === 'number' ? child : (child?.id || child);
              
              if (!childId) {
                console.warn(`⚠️ Enfant sans ID valide dans choice ${choice.label}:`, child);
                return;
              }
              
              // Si child est un objet complet avec des propriétés de section, l'ajouter directement
              if (typeof child === 'object' && child.id && (child.name || child.type || child.inputs || child.products || child.choice_items)) {
                if (!map[childId]) {
                  addSectionToMap(child);
                  console.log(`✅ Section enfant (objet complet) ajoutée à sectionsMap: ${childId} (${child.name || 'sans nom'})`);
                  // Si la section a un parent_section, l'ajouter aussi
                  if (child.parent_section && child.parent_section.id && !map[child.parent_section.id]) {
                    const parentSectionToAdd = {
                      ...child.parent_section,
                      choice_items: child.parent_section.choice_items || child.parent_section.choiceItems || []
                    };
                    map[child.parent_section.id] = parentSectionToAdd;
                    console.log(`✅ parent_section de la section enfant (objet complet) ajouté: ${child.parent_section.id}`);
                  }
                }
                return;
              }
              
              // Si cette section enfant n'est pas encore dans le map, chercher dans toutes les sections
              if (!map[childId]) {
                // Chercher récursivement dans toutes les sections des steps
                const findSectionById = (sections, targetId) => {
                  for (const s of sections) {
                    if (s.id === targetId) {
                      return s;
                    }
                    if (s.nested_sections) {
                      const found = findSectionById(s.nested_sections, targetId);
                      if (found) return found;
                    }
                    if (s.sections) {
                      const found = findSectionById(s.sections, targetId);
                      if (found) return found;
                    }
                  }
                  return null;
                };
                
                // Chercher dans tous les steps
                for (const st of project.steps) {
                  if (st.sections) {
                    const foundSection = findSectionById(st.sections, childId);
                    if (foundSection) {
                      addSectionToMap(foundSection);
                      console.log(`✅ Section enfant trouvée et ajoutée: ${foundSection.id} (${foundSection.name})`);
                      // Si la section trouvée a un parent_section, l'ajouter aussi
                      if (foundSection.parent_section && foundSection.parent_section.id && !map[foundSection.parent_section.id]) {
                        const parentSectionToAdd = {
                          ...foundSection.parent_section,
                          choice_items: foundSection.parent_section.choice_items || foundSection.parent_section.choiceItems || []
                        };
                        map[foundSection.parent_section.id] = parentSectionToAdd;
                        console.log(`✅ parent_section de la section enfant ajouté: ${foundSection.parent_section.id}`);
                        console.log(`🔍 parent_section choice_items count:`, parentSectionToAdd.choice_items?.length || 0);
                      }
                    }
                  }
                }
              }
            });
          });
        });
      });
    }

    // Troisième passe : parcourir tous les choice_items pour trouver les sections complètes dans children
    // Ces sections peuvent être des objets complets et ne pas être dans les steps
    if (project?.steps) {
      project.steps.forEach(step => {
        step.sections?.forEach(section => {
          section?.choice_items?.forEach(choice => {
            (choice.children || []).forEach(child => {
              // Si child est un objet complet avec des propriétés de section, l'ajouter à la map
              if (typeof child === 'object' && child.id && (child.name || child.type || child.inputs || child.products || child.choice_items)) {
                if (!map[child.id]) {
                  addSectionToMap(child);
                  console.log(`✅ Section enfant (objet complet depuis children) ajoutée à sectionsMap: ${child.id} (${child.name || 'sans nom'})`);
                  // Si la section a un parent_section, l'ajouter aussi
                  if (child.parent_section && child.parent_section.id && !map[child.parent_section.id]) {
                    const parentSectionToAdd = {
                      ...child.parent_section,
                      choice_items: child.parent_section.choice_items || child.parent_section.choiceItems || []
                    };
                    map[child.parent_section.id] = parentSectionToAdd;
                    console.log(`✅ parent_section de la section enfant (objet complet depuis children) ajouté: ${child.parent_section.id}`);
                  }
                }
              }
            });
          });
        });
      });
    }

    // Debug: Afficher toutes les sections avec parent_section dans le map final
    console.log(`📊 sectionsMap contient ${Object.keys(map).length} sections`);
    Object.values(map).forEach(section => {
      if (section.parent_section) {
        console.log(`📋 Section avec parent_section dans sectionsMap: ${section.id} (${section.name}), parent: ${section.parent_section.id}`);
      }
    });

    return map;
  }, [project]);

  const choiceItemsMap = useMemo(() => {
    const map = {};
    
    // Utiliser sectionsMap qui contient déjà toutes les sections récursivement
    // Cela garantit qu'on ne manque aucune section
    Object.values(sectionsMap).forEach(section => {
          section?.choice_items?.forEach(choice => {
            map[choice.id] = {
              choice,
              parentSectionId: section.id
            };
          });
    });
    
    // Ajouter aussi les choix des parent_section qui sont référencés dans les sections
    // Ces parent_section peuvent être des objets directs et ne pas être dans sectionsMap
    if (project?.steps) {
      project.steps.forEach(step => {
        step.sections?.forEach(section => {
          // Si cette section a un parent_section, ajouter les choix du parent_section
          if (section.parent_section && section.parent_section.id) {
            const parentSection = section.parent_section;
            // Si le parent_section a des choice_items, les ajouter au map
            if (parentSection.choice_items && Array.isArray(parentSection.choice_items)) {
              parentSection.choice_items.forEach(choice => {
                // Ne pas écraser si le choix est déjà dans le map
                if (!map[choice.id]) {
            map[choice.id] = {
              choice,
                    parentSectionId: parentSection.id
            };
                  console.log(`✅ Choix du parent_section ajouté à choiceItemsMap: ${choice.id} (${choice.label}), parentSectionId: ${parentSection.id}`);
                }
          });
            }
          }
        });
      });
    }
    
    return map;
  }, [sectionsMap, project]);

  const sectionsUsedAsChoiceChildren = useMemo(() => {
    const ids = new Set();
    
    // Utiliser sectionsMap qui contient déjà toutes les sections récursivement
    Object.values(sectionsMap).forEach(section => {
      // Ajouter les enfants des choice_items
          section?.choice_items?.forEach(choice => {
        (choice.children || []).forEach(child => {
          // child peut être soit un ID (string/number), soit un objet complet
          const childId = typeof child === 'string' || typeof child === 'number' ? child : (child?.id || child);
          if (childId) {
            ids.add(childId);
          }
          });
        });
      
      // Ajouter les sections qui ont un parent_section (elles sont enfants d'une section parente)
      if (section.parent_section && section.parent_section.id) {
        ids.add(section.id);
    }
    });
    
    return ids;
  }, [sectionsMap]);

  // Map pour les inputs qui peuvent être des enfants de choix
  const inputsMap = useMemo(() => {
    const map = {};
    
    // Utiliser sectionsMap qui contient déjà toutes les sections récursivement
    Object.values(sectionsMap).forEach(section => {
      section?.inputs?.forEach(input => {
        if (input.id) {
          map[input.id] = {
            input,
            parentSectionId: section.id
          };
        }
      });
    });
    
    return map;
  }, [sectionsMap]);

  // Debug: Afficher la structure des données du projet et initialiser le projet
  // Doit être après la déclaration de sectionsMap, choiceItemsMap, et inputsMap
  useEffect(() => {
    if (project) {
      console.log("Structure du projet:", project);
      console.log("Steps du projet:", project.steps);
      if (project.steps && project.steps[0]) {
        console.log("Sections de la première étape:", project.steps[0].sections);
        if (project.steps[0].sections && project.steps[0].sections[0]) {
          console.log("Produits de la première section:", project.steps[0].sections[0].products);
        }
      }
      
      // Initialiser le type de projet pour les calculs
      if (project.project_type) {
        dispatch(setProjectType(project.project_type));
        console.log("Type de projet initialisé:", project.project_type);
      }
      
      // Sauvegarder les noms de sections (inclure toutes les sections de sectionsMap)
      const sectionNames = {};
      Object.values(sectionsMap).forEach(section => {
        if (section.id && section.name) {
          sectionNames[section.id] = section.name;
        }
      });
      dispatch(setSectionNames(sectionNames));
      console.log("Noms de sections sauvegardés:", sectionNames);
      console.log("Nombre total de sections dans sectionsMap:", Object.keys(sectionsMap).length);
      console.log("Nombre total de choice items dans choiceItemsMap:", Object.keys(choiceItemsMap).length);
      console.log("Nombre total d'inputs dans inputsMap:", Object.keys(inputsMap).length);
    }
  }, [project, dispatch, sectionsMap, choiceItemsMap, inputsMap]);

  // Synchroniser selectedChoiceBySection avec Redux selectedChoices pour l'affichage des enfants
  // Doit être après la déclaration de choiceItemsMap
  useEffect(() => {
    if (project && selectedChoices.length > 0 && Object.keys(choiceItemsMap).length > 0) {
      const syncedState = {};
      selectedChoices.forEach(choice => {
        // Trouver la section parente du choix
        const choiceInfo = choiceItemsMap[choice.id];
        if (choiceInfo && choiceInfo.parentSectionId) {
          syncedState[choiceInfo.parentSectionId] = choice.id;
          console.log(`✅ Synchronisation: choix ${choice.id} -> section ${choiceInfo.parentSectionId}`);
        } else {
          console.warn(`⚠️ Choix ${choice.id} non trouvé dans choiceItemsMap`);
        }
      });
      console.log(`🔍 État synchronisé:`, syncedState);
      setSelectedChoiceBySection(prev => {
        const newState = { ...prev, ...syncedState };
        console.log(`🔍 Nouvel état selectedChoiceBySection:`, newState);
        return newState;
      });
    }
  }, [selectedChoices, project, choiceItemsMap]);

  // Fonction pour vérifier si une condition depuis une étape précédente est satisfaite
  // La condition doit correspondre EXACTEMENT au code ou à l'ID d'un choix sélectionné
  const checkConditionFromPrevStep = useMemo(() => {
    return (condition) => {
      if (!condition) return true;
      
      // Si aucun choix n'est sélectionné, la condition n'est pas satisfaite
      if (!selectedChoices || selectedChoices.length === 0) {
        console.log(`🚫 Condition "${condition}" non satisfaite: aucun choix sélectionné`);
        return false;
      }
      
      // Si condition est un objet avec step_id et choice_id
      if (typeof condition === 'object' && condition.step_id && condition.choice_id) {
        const conditionValue = condition.choice_id;
        // Vérifier d'abord par code (plus précis), puis par ID
        const isSatisfied = selectedChoices.some(choice => {
          const matchByCode = choice.code === conditionValue;
          const matchById = choice.id === conditionValue;
          return matchByCode || matchById;
        });
        console.log(`🔍 Condition objet (choice_id: ${conditionValue}): ${isSatisfied ? '✅ satisfaite' : '❌ non satisfaite'}`);
        console.log(`🔍 Choix sélectionnés:`, selectedChoices.map(c => ({ id: c.id, code: c.code, label: c.label })));
        return isSatisfied;
      }
      
      // Si condition est une string (peut être un ID ou un code, ou plusieurs conditions avec &)
      if (typeof condition === 'string') {
        // Vérifier si c'est une condition multiple avec "&" (AND)
        if (condition.includes('&')) {
          const conditions = condition.split('&').map(c => c.trim());
          console.log(`🔍 Condition multiple (AND) détectée: ${condition} → ${conditions.length} conditions`);
          
          // Toutes les conditions doivent être satisfaites (AND)
          const allSatisfied = conditions.every(cond => {
            const matchingChoice = selectedChoices.find(choice => {
              const matchByCode = choice.code === cond;
              const matchById = choice.id === cond;
              return matchByCode || matchById;
            });
            
            if (matchingChoice) {
              console.log(`   ✅ Condition "${cond}" satisfaite par:`, {
                id: matchingChoice.id,
                code: matchingChoice.code,
                label: matchingChoice.label
              });
            } else {
              console.log(`   ❌ Condition "${cond}" non satisfaite`);
            }
            
            return !!matchingChoice;
          });
          
          if (allSatisfied) {
            console.log(`✅ Toutes les conditions multiples satisfaites pour "${condition}"`);
          } else {
            console.log(`❌ Au moins une condition multiple non satisfaite pour "${condition}"`);
            console.log(`🔍 Choix sélectionnés actuellement:`, selectedChoices.map(c => ({ id: c.id, code: c.code, label: c.label })));
          }
          
          return allSatisfied;
        }
        
        // Condition simple (une seule condition)
        // Vérifier d'abord par code (plus précis et fiable), puis par ID
        const matchingChoice = selectedChoices.find(choice => {
          const matchByCode = choice.code === condition;
          const matchById = choice.id === condition;
          return matchByCode || matchById;
        });
        
        const isSatisfied = !!matchingChoice;
        
        if (isSatisfied) {
          const matchType = matchingChoice.code === condition ? 'CODE' : 'ID';
          console.log(`✅ Condition string "${condition}" satisfaite par ${matchType} du choix:`, {
            id: matchingChoice.id,
            code: matchingChoice.code,
            label: matchingChoice.label
          });
        } else {
          console.log(`❌ Condition string "${condition}" non satisfaite`);
          console.log(`🔍 Choix sélectionnés actuellement:`, selectedChoices.map(c => ({ id: c.id, code: c.code, label: c.label })));
          console.log(`🔍 Recherche du code "${condition}" dans les choix sélectionnés...`);
          const codeMatches = selectedChoices.filter(c => c.code === condition);
          const idMatches = selectedChoices.filter(c => c.id === condition);
          if (codeMatches.length > 0) {
            console.log(`   - Correspondances par CODE trouvées:`, codeMatches.map(c => ({ id: c.id, code: c.code, label: c.label })));
          }
          if (idMatches.length > 0) {
            console.log(`   - Correspondances par ID trouvées:`, idMatches.map(c => ({ id: c.id, code: c.code, label: c.label })));
          }
          if (codeMatches.length === 0 && idMatches.length === 0) {
            console.log(`   - Aucune correspondance trouvée pour "${condition}"`);
          }
        }
        
        return isSatisfied;
      }
      
      // Si condition est un nombre (ID uniquement)
      if (typeof condition === 'number') {
        const isSatisfied = selectedChoices.some(choice => choice.id === condition);
        console.log(`🔍 Condition number (ID: ${condition}): ${isSatisfied ? '✅ satisfaite' : '❌ non satisfaite'}`);
        return isSatisfied;
      }
      
      // Si condition est un tableau d'IDs ou codes (OR logic)
      if (Array.isArray(condition)) {
        const isSatisfied = condition.some(conditionValue => 
          selectedChoices.some(choice => {
            const matchByCode = choice.code === conditionValue;
            const matchById = choice.id === conditionValue;
            return matchByCode || matchById;
          })
        );
        console.log(`🔍 Condition array ${JSON.stringify(condition)}: ${isSatisfied ? '✅ satisfaite' : '❌ non satisfaite'}`);
        return isSatisfied;
      }
      
      // Par défaut, ne pas afficher si la condition n'est pas reconnue
      console.warn(`⚠️ Condition non reconnue:`, condition);
      return false;
    };
  }, [selectedChoices]);

  const handleChangeStep = (stepIndex) => {
    setActiveStep(stepIndex);
    // Réinitialiser la sélection de nested section lors du changement d'étape
    setSelectedNestedSection(null);
  };

  const handleNestedSectionClick = (nestedSection) => {
    // Si la même nested section est cliquée, la désélectionner
    if (selectedNestedSection?.id === nestedSection.id) {
      setSelectedNestedSection(null);
    } else {
      // Sinon, sélectionner la nouvelle nested section
      setSelectedNestedSection(nestedSection);
    }
  };

  const handleChoiceSelection = (sectionId, choiceId, isSelected) => {
    // Pour les sections avec choix mutuellement exclusifs, désélectionner les autres choix de la même section
    setSelectedChoiceBySection(prev => {
      const newState = { ...prev };
      if (isSelected) {
        // Sélectionner ce choix et désélectionner les autres de la même section
        newState[sectionId] = choiceId;
      } else {
        // Désélectionner ce choix
        newState[sectionId] = undefined;
      }
      return newState;
    });
  };

  const handlePrevStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleNextStep = () => {
    console.log('🔍 handleNextStep appelé:', {
      activeStep,
      totalSteps: project?.steps?.length,
      isLastStep: activeStep >= (project?.steps?.length - 1)
    });
    
    if (activeStep < project.steps.length - 1) {
      setActiveStep(activeStep + 1);
    } else {
      // Si on est à la dernière étape, rediriger vers la finalisation
      console.log('🚀 Redirection vers /finalization');
      console.log('🔍 URL actuelle avant redirection:', window.location.href);
      
      // Utiliser directement window.location.href pour forcer la navigation
      window.location.href = '/finalization';
    }
  };

  const toUpper = (sectionName) => {
    return sectionName.toUpperCase();
  };

  useEffect(() => {
    if (project && activeStep === null) {
      setActiveStep(0);
    }
  }, [project]);

  const renderChildSectionBlock = (section, keyPrefix = 'child') => {
    if (!section) return null;
    
    // Vérifier la condition condition_from_prev_step pour les sections enfants aussi
    if (section.condition_from_prev_step) {
      const shouldDisplay = checkConditionFromPrevStep(section.condition_from_prev_step);
      if (!shouldDisplay) {
        console.log(`🚫 Section ${section.id} (${section.name}) non affichée: condition_from_prev_step non satisfaite (${section.condition_from_prev_step})`);
        console.log(`🔍 Choix sélectionnés actuellement:`, selectedChoices.map(c => ({ id: c.id, code: c.code })));
        return null;
      }
      console.log(`✅ Section ${section.id} (${section.name}) affichée: condition_from_prev_step satisfaite`);
    }
    
    return (
      <Section
        key={`${keyPrefix}-${section.id}`}
        name={toUpper(section.name || 'Section')}
        description={section.description}
        classe="px-4"
        textClass="text"
        sectionClass={section.name && 'bois-de-pays'}
        content={renderSectionContent(section, true)}
      />
    );
  };

  const renderChoiceItem = (choice, parentSectionId, depth = 0) => {
    const childSections = [];
    const childChoices = [];
    const childInputs = [];

    (choice.children || []).forEach(child => {
      // child peut être soit un ID (string/number), soit un objet complet
      const childId = typeof child === 'string' || typeof child === 'number' ? child : (child?.id || child);
      
      if (!childId) {
        console.warn(`⚠️ Enfant sans ID valide dans choice ${choice.label}:`, child);
        return;
      }
      
      // Si child est un objet complet avec des propriétés de section, l'utiliser directement
      if (typeof child === 'object' && child.id && (child.name || child.type || child.inputs || child.products || child.choice_items)) {
        // Vérifier si la section est déjà dans sectionsMap
      if (sectionsMap[childId]) {
          const childSection = sectionsMap[childId];
          childSections.push(childSection);
          console.log(`✅ Section enfant (objet complet) trouvée dans sectionsMap: ${childId} (${childSection.name})`);
        } else {
          // Utiliser l'objet directement comme section
          childSections.push(child);
          console.log(`✅ Section enfant (objet complet) utilisée directement: ${childId} (${child.name || 'sans nom'})`);
        }
        return;
      }
      
      // Sinon, chercher dans les maps
      if (sectionsMap[childId]) {
        const childSection = sectionsMap[childId];
        childSections.push(childSection);
        console.log(`✅ Section enfant trouvée dans sectionsMap: ${childId} (${childSection.name}), parent_section: ${childSection.parent_section?.id || 'none'}, condition: ${childSection.condition_from_prev_step || 'none'}`);
      } else if (choiceItemsMap[childId]) {
        childChoices.push(choiceItemsMap[childId]);
      } else if (inputsMap[childId]) {
        childInputs.push(inputsMap[childId]);
      } else {
        console.warn(`⚠️ Enfant introuvable pour l'ID ${childId} (choice ${choice.label})`);
        console.warn(`⚠️ Type de child:`, typeof child, 'Valeur:', child);
        // Debug: Afficher plus d'informations pour les sections avec parent_section
        if (project?.steps) {
          console.log(`🔍 Recherche de la section ${childId} dans le projet...`);
          project.steps.forEach((step, stepIndex) => {
            step.sections?.forEach((section, sectionIndex) => {
              if (section.id === childId) {
                console.log(`✅ Section trouvée dans step ${stepIndex}, section ${sectionIndex}:`, section);
              }
              if (section.parent_section && section.parent_section.id === childId) {
                console.log(`✅ Section avec parent_section trouvée dans step ${stepIndex}, section ${sectionIndex}:`, section);
              }
            });
          });
        }
        // Vérifier si la section est dans sectionsMap mais avec un ID différent
        const childIdStr = String(childId);
        if (childIdStr && childIdStr !== '[object Object]') {
          console.log(`🔍 Vérification de sectionsMap pour ${childId}:`, Object.keys(sectionsMap).filter(key => key.includes(childIdStr.substring(0, Math.min(8, childIdStr.length)))));
        }
      }
    });

    const isChoiceSelected = selectedChoiceBySection[parentSectionId] === choice.id;
    
    // Debug: Afficher l'état de sélection pour les choix avec des enfants
    if (choice.children && choice.children.length > 0) {
      console.log(`🔍 Choice ${choice.id} (${choice.label}): isSelected=${isChoiceSelected}, parentSectionId=${parentSectionId}, selectedChoiceBySection[${parentSectionId}]=${selectedChoiceBySection[parentSectionId]}, choice.id=${choice.id}`);
      console.log(`🔍 selectedChoiceBySection:`, selectedChoiceBySection);
      console.log(`🔍 Redux selectedChoices:`, selectedChoices.map(c => ({ id: c.id, code: c.code })));
    }

    // Vérifier si le choix a des produits associés (product_ref ou linked_product)
    const hasProducts = (choice.product_ref || choice.linked_product);
    const productsToDisplay = [];
    
    if (hasProducts) {
      // Ajouter le produit principal si présent
      if (choice.product_ref) {
        productsToDisplay.push({
          productId: choice.product_ref,
          isLinked: false
        });
      }
      // Ajouter le produit lié si présent
      if (choice.linked_product) {
        productsToDisplay.push({
          productId: choice.linked_product,
          isLinked: true
        });
      }
    }

    return (
      <React.Fragment key={choice.id}>
        <ChoiceItem
          choice={choice}
          sectionId={parentSectionId}
          onSelect={(isSelected) => handleChoiceSelection(parentSectionId, choice.id, isSelected)}
          forceSelected={selectedChoiceBySection[parentSectionId] !== undefined ? isChoiceSelected : undefined}
        />
        {isChoiceSelected && (
          <>
            {/* Afficher les produits associés au choix si sélectionné */}
            {hasProducts && productsToDisplay.length > 0 && (
              <div className="col-12 choice-products-wrapper" style={{ marginTop: '1rem' }}>
                <div className="row">
                  {productsToDisplay.map(({ productId, isLinked }, index) => (
                    <ProductWrapper
                      key={`choice-product-${choice.id}-${productId}-${index}`}
                      productId={productId}
                      productType={null}
                      section={parentSectionId}
                      contentProductId={`choice-${choice.id}-product-${productId}`}
                    />
                  ))}
                  </div>
              </div>
            )}
            
            {/* Afficher les enfants du choix (sections, choix, inputs) */}
            {(childSections.length > 0 || childChoices.length > 0 || childInputs.length > 0) && (
              <div className="col-12 choice-children-wrapper" style={{ marginLeft: depth * 20, marginTop: '1rem' }}>
            <div className="border-start ps-3 ms-2 my-3">
              {childSections.length > 0 && (
                    <div className="row">
                  {childSections.map(childSection => (
                        <div key={`choice-child-section-${choice.id}-${childSection.id}`} className="col-12 my-3">
                      {renderChildSectionBlock(childSection, `choice-child-${choice.id}`)}
                    </div>
                  ))}
                    </div>
              )}

                  {childInputs.length > 0 && (
                    <div className="row mt-3">
                      {childInputs.map(({ input, parentSectionId: inputParentSectionId }) => {
                        // Vérifier si l'input enfant a une condition
                        if (input.condition_from_prev_step) {
                          const shouldDisplay = checkConditionFromPrevStep(input.condition_from_prev_step);
                          if (!shouldDisplay) {
                            return null;
                          }
                        }
                        
                        return (
                          <div key={`choice-child-input-${choice.id}-${input.id}`} className="col-12 my-3">
                            <Input
                              label={input.input_label}
                              info={input.input_info}
                              description={input.description}
                              unity={input.input_unity}
                              type={input.input_type}
                              section={inputParentSectionId}
                              inputKey={input.input_label || 'input'}
                            />
                  </div>
                        );
                      })}
                    </div>
              )}

              {childChoices.length > 0 && (
                    <div className="row mt-3">
                  {childChoices.map(({ choice: childChoice, parentSectionId: childParentSectionId }) => (
                        <div key={`choice-child-choice-${childChoice.id}`} className="col-12">
                      {renderChoiceItem(childChoice, childParentSectionId, depth + 1)}
                    </div>
                  ))}
                    </div>
              )}
            </div>
          </div>
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  const renderSectionContent = (section, isChildSection = false) => {
    // Si cette section a un parent_section, afficher le parent_section et ses choix
    // Le parent_section contient les choix qui contrôlent l'affichage des sections enfants
    // Mais seulement si cette section n'est pas déjà affichée comme enfant d'un choix (pour éviter la récursion)
    // ET seulement si la section n'est pas dans sectionsUsedAsChoiceChildren (car le parent_section sera affiché dans la boucle principale)
    let parentSectionToDisplay = null;
    if (section.parent_section && !isChildSection && !sectionsUsedAsChoiceChildren.has(section.id)) {
      console.log(`🔍 Section ${section.id} (${section.name}) a un parent_section: ${section.parent_section.id}`);
      // Si parent_section est un objet avec un id, chercher dans sectionsMap
      if (section.parent_section.id && sectionsMap[section.parent_section.id]) {
        parentSectionToDisplay = sectionsMap[section.parent_section.id];
        console.log(`✅ parent_section trouvé dans sectionsMap: ${parentSectionToDisplay.id} (${parentSectionToDisplay.name})`);
        console.log(`🔍 parent_section choice_items count:`, parentSectionToDisplay.choice_items?.length || 0);
      } else if (section.parent_section.id) {
        console.log(`⚠️ parent_section ${section.parent_section.id} non trouvé dans sectionsMap, recherche dans les steps...`);
        // Si le parent_section n'est pas dans sectionsMap, utiliser l'objet parent_section directement
        // Mais d'abord, essayer de le trouver dans les sections des steps
        const findParentSection = () => {
          if (!project?.steps) return null;
          for (const step of project.steps) {
            if (step.sections) {
              for (const s of step.sections) {
                if (s.id === section.parent_section.id) {
                  return s;
                }
                // Chercher récursivement
                const findRecursive = (sec) => {
                  if (sec.id === section.parent_section.id) return sec;
                  if (sec.nested_sections) {
                    for (const ns of sec.nested_sections) {
                      const found = findRecursive(ns);
                      if (found) return found;
                    }
                  }
                  if (sec.sections) {
                    for (const childSec of sec.sections) {
                      const found = findRecursive(childSec);
                      if (found) return found;
                    }
                  }
                  return null;
                };
                const found = findRecursive(s);
                if (found) return found;
              }
            }
          }
          return null;
        };
        const foundParent = findParentSection();
        parentSectionToDisplay = foundParent || section.parent_section;
        if (foundParent) {
          console.log(`✅ parent_section trouvé dans les steps: ${foundParent.id} (${foundParent.name})`);
          console.log(`🔍 parent_section choice_items count:`, foundParent.choice_items?.length || 0);
        } else {
          console.log(`⚠️ parent_section non trouvé, utilisation de l'objet parent_section directement`);
          console.log(`🔍 parent_section (objet direct) choice_items count:`, section.parent_section.choice_items?.length || 0);
          // Si on utilise l'objet parent_section directement, s'assurer qu'il a bien les choice_items
          if (section.parent_section.choice_items && section.parent_section.choice_items.length > 0) {
            console.log(`✅ parent_section (objet direct) a des choice_items`);
          }
        }
      }
    }
    
    return (
    <>
        {/* Afficher le parent_section s'il existe et contient des choix */}
        {parentSectionToDisplay && (
          <>
            {/* Vérifier la condition du parent_section */}
            {(() => {
              const hasCondition = parentSectionToDisplay.condition_from_prev_step;
              const conditionSatisfied = !hasCondition || checkConditionFromPrevStep(parentSectionToDisplay.condition_from_prev_step);
              console.log(`🔍 parent_section ${parentSectionToDisplay.id} (${parentSectionToDisplay.name}): hasCondition=${hasCondition}, conditionSatisfied=${conditionSatisfied}`);
              console.log(`🔍 parent_section condition:`, parentSectionToDisplay.condition_from_prev_step);
              console.log(`🔍 parent_section choice_items:`, parentSectionToDisplay.choice_items?.length || 0);
              console.log(`🔍 Choix sélectionnés actuellement:`, selectedChoices.map(c => ({ id: c.id, code: c.code, label: c.label })));
              if (!conditionSatisfied) {
                console.log(`🚫 parent_section non affiché: condition non satisfaite (${parentSectionToDisplay.condition_from_prev_step})`);
                console.log(`🔍 Choix sélectionnés:`, selectedChoices.map(c => ({ id: c.id, code: c.code })));
                console.log(`🔍 Recherche du code "${parentSectionToDisplay.condition_from_prev_step}" dans les choix sélectionnés...`);
                const matchingChoice = selectedChoices.find(c => 
                  c.code === parentSectionToDisplay.condition_from_prev_step || 
                  c.id === parentSectionToDisplay.condition_from_prev_step
                );
                if (matchingChoice) {
                  console.log(`✅ Choix correspondant trouvé:`, matchingChoice);
                } else {
                  console.log(`❌ Aucun choix correspondant trouvé pour "${parentSectionToDisplay.condition_from_prev_step}"`);
                }
              }
              return conditionSatisfied;
            })() && (
              <div className="mb-4">
                <h4 className="mb-3">{toUpper(parentSectionToDisplay.name || '')}</h4>
                {parentSectionToDisplay.description && (
                  <p className="text-muted mb-3">{parentSectionToDisplay.description}</p>
                )}
                {/* Afficher les choice_items du parent_section */}
                {parentSectionToDisplay.choice_items && parentSectionToDisplay.choice_items.length > 0 ? (
                  <div className="row">
                    {parentSectionToDisplay.choice_items.map(choice => {
                      // Vérifier si le choix a une condition condition_from_prev_step
                      if (choice.condition_from_prev_step) {
                        const shouldDisplay = checkConditionFromPrevStep(choice.condition_from_prev_step);
                        if (!shouldDisplay) {
                          console.log(`🚫 Choice ${choice.id} (${choice.label}) du parent_section non affiché: condition_from_prev_step non satisfaite (${choice.condition_from_prev_step})`);
                          return null;
                        }
                        console.log(`✅ Choice ${choice.id} (${choice.label}) du parent_section affiché: condition_from_prev_step satisfaite`);
                      }
                      return (
                        <React.Fragment key={choice.id}>
                          {renderChoiceItem(choice, parentSectionToDisplay.id)}
                        </React.Fragment>
                      );
                    })}
                  </div>
                ) : (
                  console.warn(`⚠️ parent_section ${parentSectionToDisplay.id} n'a pas de choice_items`)
                )}
              </div>
            )}
          </>
        )}

      {/* Afficher les choice_items (ou choices si choice_items n'existe pas) */}
      {((section?.choice_items && section.choice_items.length > 0) || (section?.choices && section.choices.length > 0)) && (
        (section.choice_items || section.choices || []).map(choice => {
          // Vérifier si le choix a une condition condition_from_prev_step
          if (choice.condition_from_prev_step) {
            const shouldDisplay = checkConditionFromPrevStep(choice.condition_from_prev_step);
            if (!shouldDisplay) {
              console.log(`🚫 Choice ${choice.id} (${choice.label}) non affiché: condition_from_prev_step non satisfaite (${choice.condition_from_prev_step})`);
              return null;
            }
            console.log(`✅ Choice ${choice.id} (${choice.label}) affiché: condition_from_prev_step satisfaite`);
          }
          return renderChoiceItem(choice, section.id);
        })
      )}

      {section?.products?.length > 0 && (
          section.products.map((product) => {
            // Vérifier si le produit a une condition condition_from_prev_step
            if (product.condition_from_prev_step) {
              const shouldDisplay = checkConditionFromPrevStep(product.condition_from_prev_step);
              if (!shouldDisplay) {
                return null;
              }
            }
            
            return (
          <ProductWrapper
            key={product.id}
            productId={product.product}
            productType={product.product_type}
            section={section.id}
            contentProductId={product.id}
          />
            );
          })
      )}

      {section?.nested_sections?.length > 0 && (
        section.nested_sections.map((nestedSection) => (
          <Nested
            key={nestedSection.id}
            label={nestedSection.name.toUpperCase()}
            alt={nestedSection.name}
            imageUrl={nestedSection.image || '/assets/images/Beton-plot.png'}
            nestedSection={nestedSection}
            isSelected={selectedNestedSection?.id === nestedSection.id}
            onNestedClick={handleNestedSectionClick}
          />
        ))
      )}

      {section?.inputs && Array.isArray(section.inputs) && section.inputs.length > 0 && (
          section.inputs.map((input) => {
            // Vérifier si l'input a une condition condition_from_prev_step
            if (input.condition_from_prev_step) {
              const shouldDisplay = checkConditionFromPrevStep(input.condition_from_prev_step);
              if (!shouldDisplay) {
                return null;
              }
            }
            
            return (
          <Input
            key={input.id}
            label={input.input_label}
            info={input.input_info}
            description={input.description}
            unity={input.input_unity}
            type={input.input_type}
            section={section.id}
            inputKey={input.input_label || 'input'}
          />
            );
          })
      )}

      {section?.images?.length > 0 && (
        section.images.map((image) => (
          <SectionImage
            key={image.id}
            src={image.file}
          />
        ))
      )}
    </>
  );
  };

  // Sauvegarder les noms de sections des nested sections dans Redux
  useEffect(() => {
    if (selectedNestedSection && selectedNestedSection.sections) {
      const newSectionNames = { ...sectionNames };
      selectedNestedSection.sections.forEach(nestedSectionItem => {
        if (nestedSectionItem.id && nestedSectionItem.name) {
          newSectionNames[nestedSectionItem.id] = nestedSectionItem.name;
        }
      });
      if (Object.keys(newSectionNames).length > Object.keys(sectionNames).length) {
        dispatch(setSectionNames(newSectionNames));
      }
    }
  }, [selectedNestedSection, sectionNames, dispatch]);


  return (
    <>
      <div>
        {
          isLoading && <p className="text-center">Chargement...</p>
        }
      </div> 
      <Banner projectName={String(project?.name).replace('_', ' ')} />
      <section className="page-banner">
        <div className="container">
          <div className="d-flex">
            <div className="m-auto">
              {/* <h3 class="page-title">BOIS DE CONSTRUCTION</h3> */}
              <div className="breadcrumbss">
                {
                  project?.steps?.map((step, index) => (
                    <Link to="#" key={step.id} onClick={() => handleChangeStep(index)}>
                      <div className={`breadcrumb-item ${activeStep === index ? 'active' : activeStep > index ? 'passed' : ''}`}>{step.name}</div>
                    </Link>     
                  ))
                }
                <Link to="/finalization">
                  <div className="breadcrumb-item">Finalisation</div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* List sections */}
      <section className="container">
          {
            project?.steps?.map((step, index) => {

              return(
                <div key={`stepList-${step.id || index}`} className={`steps-list ${activeStep === index ? 'd-block' : 'd-none'}`}>
                  
                  <StepTitle
                    title={step.description}
                  />
                  
                  {(() => {
                    // Set pour suivre les parent_section déjà affichés (éviter les doublons)
                    const displayedParentSections = new Set();
                    
                    // Trier les sections par order avant de les afficher
                    // Les sections avec order: 0 seront affichées en premier
                    const sortedSections = [...(step?.sections || [])].sort((a, b) => {
                      const orderA = a.order !== undefined ? a.order : 999;
                      const orderB = b.order !== undefined ? b.order : 999;
                      return orderA - orderB;
                    });
                    
                    // Log pour debug : afficher l'ordre des sections
                    console.log(`📋 Sections triées par order pour l'étape "${step.name}":`, sortedSections.map(s => ({
                      id: s.id,
                      name: s.name,
                      order: s.order,
                      condition: s.condition_from_prev_step
                    })));
                    
                    return sortedSections.map((section) => {
                      // PRIORITÉ 1: Si cette section a un parent_section, afficher le parent_section si la condition est satisfaite
                      // mais ne pas afficher la section elle-même (elle sera affichée comme enfant d'un choix)
                      if (section.parent_section && section.parent_section.id) {
                        const parentSectionId = section.parent_section.id;
                        
                        // Si ce parent_section a déjà été affiché, ne pas l'afficher à nouveau
                        if (displayedParentSections.has(parentSectionId)) {
                          // Mais vérifier si cette section elle-même doit être affichée (si elle n'est pas un enfant de choix)
                          // Si la section n'est pas dans sectionsUsedAsChoiceChildren, elle peut être affichée directement
                          if (!sectionsUsedAsChoiceChildren.has(section.id)) {
                            // Vérifier la condition de la section elle-même
                            if (section.condition_from_prev_step) {
                              const shouldDisplay = checkConditionFromPrevStep(section.condition_from_prev_step);
                              if (!shouldDisplay) {
                                return null;
                              }
                            }
                            // Afficher la section directement
                            return (
                              <React.Fragment key={section.id}>
                                <Section
                                  name={toUpper(section.name || '')}
                                  description={section.description}
                                  classe="px-4"
                                  textClass="text"
                                  sectionClass="bois-de-pays"
                                  content={renderSectionContent(section, false)}
                                />
                              </React.Fragment>
                            );
                          }
                          return null;
                        }
                        
                        // Chercher le parent_section dans sectionsMap ou utiliser l'objet directement
                        let parentSection = sectionsMap[parentSectionId];
                        
                        // Si le parent_section n'est pas dans sectionsMap, utiliser l'objet parent_section directement
                        if (!parentSection) {
                          parentSection = section.parent_section;
                          console.log(`⚠️ parent_section ${parentSectionId} non trouvé dans sectionsMap, utilisation de l'objet parent_section directement`);
                        }
                        
                        // S'assurer que le parent_section a bien ses choice_items
                        if (parentSection && !parentSection.choice_items && section.parent_section.choice_items) {
                          parentSection = {
                            ...parentSection,
                            choice_items: section.parent_section.choice_items
                          };
                          console.log(`✅ choice_items ajoutés au parent_section depuis section.parent_section`);
                        }
                        
                        const hasCondition = parentSection?.condition_from_prev_step;
                        const conditionSatisfied = !hasCondition || checkConditionFromPrevStep(parentSection?.condition_from_prev_step);
                        
                        console.log(`🔍 Section ${section.id} (${section.name}) a un parent_section ${parentSectionId}:`);
                        console.log(`  - hasCondition: ${hasCondition}`);
                        console.log(`  - condition: ${parentSection.condition_from_prev_step}`);
                        console.log(`  - conditionSatisfied: ${conditionSatisfied}`);
                        console.log(`  - selectedChoices:`, selectedChoices.map(c => ({ id: c.id, code: c.code, label: c.label })));
                        console.log(`  - choice_items count: ${parentSection.choice_items?.length || 0}`);
                        
                        if (conditionSatisfied && parentSection.choice_items && parentSection.choice_items.length > 0) {
                          // Marquer ce parent_section comme affiché
                          displayedParentSections.add(parentSectionId);
                          
                          // Afficher le parent_section avec ses choice_items
                          return (
                            <React.Fragment key={`parent-section-${parentSection.id}`}>
                              <Section
                                name={toUpper(parentSection.name || '')}
                                description={parentSection.description}
                                classe="px-4"
                                textClass="text"
                                sectionClass="bois-de-pays"
                                content={
                                  <div className="row">
                                    {parentSection.choice_items.map(choice => {
                                      // Vérifier si le choix a une condition condition_from_prev_step
                                      if (choice.condition_from_prev_step) {
                                        const shouldDisplay = checkConditionFromPrevStep(choice.condition_from_prev_step);
                                        if (!shouldDisplay) {
                                          console.log(`🚫 Choice ${choice.id} (${choice.label}) du parent_section non affiché: condition_from_prev_step non satisfaite (${choice.condition_from_prev_step})`);
                                          return null;
                                        }
                                        console.log(`✅ Choice ${choice.id} (${choice.label}) du parent_section affiché: condition_from_prev_step satisfaite`);
                                      }
                                      return (
                                        <React.Fragment key={choice.id}>
                                          {renderChoiceItem(choice, parentSection.id)}
                                        </React.Fragment>
                                      );
                                    })}
                                  </div>
                                }
                              />
                            </React.Fragment>
                          );
                        }
                        // Si la condition n'est pas satisfaite ou pas de choice_items, vérifier si la section elle-même doit être affichée
                        // (si elle n'est pas un enfant de choix et a sa propre condition)
                        if (!sectionsUsedAsChoiceChildren.has(section.id)) {
                          // Vérifier la condition de la section elle-même
                          if (section.condition_from_prev_step) {
                            const shouldDisplay = checkConditionFromPrevStep(section.condition_from_prev_step);
                            if (!shouldDisplay) {
                              console.log(`🚫 Section ${section.id} (${section.name}) non affichée: condition_from_prev_step non satisfaite (${section.condition_from_prev_step})`);
                              return null;
                            }
                          }
                          // Afficher la section directement
                          return (
                            <React.Fragment key={section.id}>
                              <Section
                                name={toUpper(section.name || '')}
                                description={section.description}
                                classe="px-4"
                                textClass="text"
                                sectionClass="bois-de-pays"
                                content={renderSectionContent(section, false)}
                              />
                            </React.Fragment>
                          );
                        }
                        console.log(`🚫 parent_section ${parentSectionId} non affiché: condition non satisfaite ou pas de choice_items`);
                        return null;
                      }
                    
                    // PRIORITÉ 2: Ignorer les sections qui sont des enfants de choix (elles seront affichées via renderChoiceItem)
                    if (sectionsUsedAsChoiceChildren.has(section.id)) {
                      return null;
                    }
                    
                    // IMPORTANT: Si la section a des choice_items (dans choices ou choice_items), s'assurer qu'ils sont bien dans choice_items
                    // Certaines sections ont choice_items dans le champ "choices" au lieu de "choice_items"
                    if (section.choices && Array.isArray(section.choices) && section.choices.length > 0) {
                      if (!section.choice_items || section.choice_items.length === 0) {
                        // Convertir choices en choice_items si nécessaire
                        section.choice_items = section.choices;
                        console.log(`✅ choice_items ajoutés depuis choices pour section ${section.id} (${section.name}): ${section.choice_items.length} choix`);
                      }
                    }
                    
                    // Vérifier la condition condition_from_prev_step
                    if (section.condition_from_prev_step) {
                      const shouldDisplay = checkConditionFromPrevStep(section.condition_from_prev_step);
                      console.log(`🔍 Section "${section.name}" (${section.id}): condition_from_prev_step="${section.condition_from_prev_step}", shouldDisplay=${shouldDisplay}`);
                      if (!shouldDisplay) {
                        console.log(`🚫 Section "${section.name}" non affichée: condition "${section.condition_from_prev_step}" non satisfaite`);
                        console.log(`🔍 Choix sélectionnés actuellement:`, selectedChoices.map(c => ({ id: c.id, code: c.code, label: c.label })));
                        // Debug supplémentaire pour les types de bardage
                        if (section.condition_from_prev_step === 'TYPE_LAMES_FIBRES_CIMENT' || section.condition_from_prev_step === 'TYPE_PANNEAUX_FIBRES_CIMENT' || section.condition_from_prev_step === 'TYPE_LAMES_BOIS') {
                          console.warn(`⚠️ Section "${section.name}" avec condition "${section.condition_from_prev_step}" non satisfaite. Vérifiez que le choix correspondant est sélectionné.`);
                          const matchingChoice = selectedChoices.find(c => 
                            c.code === section.condition_from_prev_step || 
                            c.id === section.condition_from_prev_step
                          );
                          if (!matchingChoice) {
                            console.warn(`⚠️ Aucun choix trouvé avec code/id "${section.condition_from_prev_step}" dans les choix sélectionnés.`);
                          }
                        }
                        return null;
                      }
                      console.log(`✅ Section "${section.name}" affichée: condition "${section.condition_from_prev_step}" satisfaite`);
                    }
                    
                    // Debug: Afficher la structure des sections
                    console.log('🔍 Section:', section.name, 'Inputs:', section.inputs);
                    
                    // Vérifier si cette section a une nested_section sélectionnée
                    const hasSelectedNested = selectedNestedSection && 
                      section?.nested_sections?.some(ns => ns.id === selectedNestedSection.id);
                    
                    // Récupérer les sections de la nested_section sélectionnée si elle appartient à cette section
                    // Trier par order si disponible
                    const nestedSectionsToDisplay = hasSelectedNested && selectedNestedSection.sections 
                      ? [...selectedNestedSection.sections].sort((a, b) => (a.order || 0) - (b.order || 0))
                      : [];
                    
                    return(
                      <React.Fragment key={section.id}>
                        <Section
                          name={toUpper(section.name)}
                          description={section.description}
                          classe={section?.nested_sections?.length > 0 ? 'px-5' : section?.name === 'Taille de votre terrasse' ? 'px-5' : ''}
                          textClass={section?.nested_sections?.length > 0 ? 'text' : section?.name === 'Taille de votre terrasse' ? 'text' : 'text-2'}
                          sectionClass={section.name && 'bois-de-pays'}
                          content={renderSectionContent(section)}
                        />
                        
                        
                        {/* Afficher les sections de la nested_section sélectionnée */}
                        {hasSelectedNested && nestedSectionsToDisplay.length > 0 && (
                          <div className="mt-4 mb-4" style={{ marginLeft: '2rem', borderLeft: '3px solid #B39C81', paddingLeft: '1rem' }}>
                            {nestedSectionsToDisplay.map((nestedSectionItem) => {
                              // Vérifier la condition condition_from_prev_step pour les nested sections aussi
                              if (nestedSectionItem.condition_from_prev_step) {
                                const shouldDisplay = checkConditionFromPrevStep(nestedSectionItem.condition_from_prev_step);
                                if (!shouldDisplay) {
                                  return null;
                                }
                              }
                              
                              return (
                                <Section
                                  key={nestedSectionItem.id}
                                  name={toUpper(nestedSectionItem.name || selectedNestedSection.name)}
                                  description={nestedSectionItem.description || selectedNestedSection.description}
                                  classe="px-5"
                                  textClass="text"
                                  sectionClass="bois-de-pays"
                                  content={
                                    <>
                                      {
                                        nestedSectionItem?.choice_items?.length > 0 && (
                                          nestedSectionItem.choice_items.map(choice => (
                                            // use renderChoiceItem to preserve child rendering logic
                                            renderChoiceItem(choice, nestedSectionItem.id)
                                          ))
                                        )
                                      }

                                      {
                                        nestedSectionItem?.products?.length > 0 && (
                                          nestedSectionItem.products.map((product) => (
                                            <NestedProductWrapper
                                              key={product.id}
                                              productId={product.product}
                                              productType={product.product_type}
                                              section={nestedSectionItem.id}
                                              contentProductId={product.id}
                                            />
                                          ))
                                        )
                                      }

                                      {
                                        nestedSectionItem?.images?.length > 0 && (
                                          nestedSectionItem.images.map((image) => (
                                            <SectionImage
                                              key={image.id}
                                              src={image.file}
                                            />
                                          ))
                                        )
                                      }

                                      {
                                        nestedSectionItem?.inputs && Array.isArray(nestedSectionItem.inputs) && nestedSectionItem.inputs.length > 0 && (
                                          nestedSectionItem.inputs.map((input) => (
                                            <Input
                                              key={input.id}
                                              label={input.input_label}
                                              info={input.input_info}
                                              description={input.description}
                                              unity={input.input_unity}
                                              type={input.input_type}
                                              section={nestedSectionItem.id}
                                              inputKey={input.input_label || 'input'}
                                            />
                                          ))
                                        )
                                      }
                                    </>
                                  }
                                />
                              );
                            })}
                          </div>
                        )}
                      </React.Fragment>
                    )
                  });
                })()}
                </div>
              )
            })
          }
      </section>
      
      <div className="m-auto">
        <div className="d-flex justify-content-center mb-5">
          <div className="bois-de-pays px-3 me-2">
            <span className="text-2 span">
              <Link to="#" className='d-flex  align-items-center' onClick={handlePrevStep}>
                <FaLongArrowAltLeft className='me-2' />  MODIFIEZ
              </Link>
            </span>
          </div>
          <div className="btn btn-secondary px-3 rounded-0 d-flex align-items-center">
            <span className="text-2 " >
              <Link to="#" className="d-flex align-items-center " onClick={handleNextStep}>
                SUIVANT  <FaLongArrowAltRight  className='ms-2'/>
              </Link>
            </span>
          </div>
        </div>
      </div>
      
    </>
  );
};

export default Project;
